import Dialog from 'components/common/Dialog';
import React, {
  createContext,
  useCallback,
  useMemo,
  useState, ReactNode,
} from 'react';

type DialogProps = {
  title?: string;
  className?: string;
  cancelText?: string;
  confirmText?: string;
  onConfirm?: () => void;
  onClose?: () => void;
  disableConfirmButton?: boolean;
  disableBackdropClick?: boolean;
  content: ReactNode;
  showActions?: boolean;
  hideCloseBtn?: boolean;
  dialogClassName?: string;
  closeBtnClasses?: string;
  titleClassName?: string;
};

type ContextValues = {
  openDialog: (props: DialogProps) => void;
  closeDialog: () => void;
};

export const ConfirmationDialogContext = createContext<ContextValues>({
  openDialog: () => {},
  closeDialog: () => {},
});

function ConfirmationDialogProvider({ children }: { children: ReactNode }) {
  const DEFAULT_PROPS = {
    title: '',
    onConfirm: () => {},
    content: null,
    showActions: true,
  };
  const [props, setProps] = useState<DialogProps>(DEFAULT_PROPS);
  const [open, setOpen] = useState<boolean>(false);

  const openDialog = (_props: DialogProps) => {
    setProps({
      ...DEFAULT_PROPS,
      ..._props,
    });
    setOpen(true);
  };

  const closeDialog = useCallback(() => setOpen(false), []);

  const values = useMemo(() => ({
    openDialog,
    closeDialog,
  }), [openDialog, closeDialog]);

  return (
    <ConfirmationDialogContext.Provider value={values}>
      <Dialog
        closeBtnClasses={props.closeBtnClasses}
        dialogClassName={props.dialogClassName}
        className={props.className}
        title={props.title}
        titleClassName={props.titleClassName}
        open={open}
        onClose={props.onClose ?? closeDialog}
        disableBackdropClick={props.disableBackdropClick}
        onConfirm={props.onConfirm}
        confirmText={props.confirmText}
        cancelText={props.cancelText}
        disableConfirmButton={props.disableConfirmButton}
        content={props.content}
        showActions={props.showActions}
        hideCloseBtn={props.hideCloseBtn}
      />
      {children}
    </ConfirmationDialogContext.Provider>
  );
}

export default ConfirmationDialogProvider;
